import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, Calendar, User, Share2, ArrowRight, Clock } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
const ArticlePage = () => {
  const {
    articleId
  } = useParams();
  // Sample articles data - this would typically come from an API or CMS
  const articles = {
    'microbiome-gut-health-connection': {
      id: 'microbiome-gut-health-connection',
      title: 'The Microbiome-Gut-Brain Connection: New Research Insights',
      date: 'September 15, 2023',
      author: 'Dr. Sarah Chen',
      authorTitle: 'Research Director at CalMiR',
      readTime: '8 min read',
      featuredImage: 'https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      tags: ['Gut Health', 'Microbiome', 'Research', 'Brain Health'],
      excerpt: 'Recent studies reveal fascinating connections between gut microbiome health and cognitive function, with implications for mental wellness and neurological health.',
      content: [{
        type: 'paragraph',
        text: 'The scientific community has long suspected a connection between gut health and brain function, often referred to as the "gut-brain axis." Recent research from the California Institute for Microbiome Research (CalMiR) has uncovered compelling evidence that strengthens our understanding of this vital connection.'
      }, {
        type: 'heading',
        text: 'Breakthrough Findings'
      }, {
        type: 'paragraph',
        text: 'In a groundbreaking study published last month in the Journal of Microbiome Science, researchers at CalMiR demonstrated that specific bacterial strains in the gut microbiome produce neurotransmitter precursors that can influence cognitive function, mood regulation, and stress response.'
      }, {
        type: 'paragraph',
        text: 'The study, which involved both animal models and human participants, found that disruptions to these bacterial communities could be linked to measurable changes in cognitive performance and emotional regulation.'
      }, {
        type: 'quote',
        text: '"What\'s particularly exciting about these findings is that they suggest targeted microbiome interventions could potentially support brain health and function in ways we hadn\'t previously considered possible," explains Dr. Sarah Chen, Research Director at CalMiR and lead author of the study.'
      }, {
        type: 'heading',
        text: 'The Biofilm Connection'
      }, {
        type: 'paragraph',
        text: 'One of the most intriguing aspects of the research involves the role of biofilms—structured communities of microorganisms that adhere to surfaces within the gut. The study found that certain pathogenic biofilms can create barriers that prevent beneficial bacteria from colonizing the gut and producing important neurochemical compounds.'
      }, {
        type: 'paragraph',
        text: "This discovery helps explain why traditional probiotics sometimes fail to produce expected benefits: they simply can't establish themselves in an environment dominated by entrenched biofilms."
      }, {
        type: 'image',
        url: 'https://images.unsplash.com/photo-1559757175-7cb056fba93d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
        caption: 'Microscopic image of bacterial biofilms forming protective structures'
      }, {
        type: 'heading',
        text: 'Implications for Health and Wellness'
      }, {
        type: 'paragraph',
        text: 'These findings have significant implications for both clinical practice and consumer health products. By understanding the mechanisms through which gut bacteria influence brain function, researchers can develop more targeted interventions for conditions ranging from anxiety and depression to cognitive decline.'
      }, {
        type: 'paragraph',
        text: "CalBiome's research and development team is already working to translate these insights into practical applications. The Microbiota Reset Protocol, which combines biofilm disruptors with carefully selected probiotic strains, represents one such application of this emerging research."
      }, {
        type: 'heading',
        text: 'Looking Forward'
      }, {
        type: 'paragraph',
        text: 'As research in this area continues to evolve, we anticipate even more refined approaches to microbiome modulation for cognitive and emotional wellness. The next phase of research at CalMiR will focus on identifying specific bacterial strains and metabolites that have the most significant impact on neurological health.'
      }, {
        type: 'paragraph',
        text: 'For consumers interested in supporting their gut-brain connection, Dr. Chen recommends a multi-faceted approach that includes dietary diversity, stress management, adequate sleep, and targeted supplementation when appropriate.'
      }, {
        type: 'paragraph',
        text: '"We\'re just beginning to understand the full complexity of the microbiome-gut-brain connection," says Dr. Chen. "But what\'s clear is that caring for your gut microbiome is an essential component of caring for your brain."'
      }],
      references: ['Chen, S. et al. (2023). "Microbiome Modulation of Neurochemical Pathways: Implications for Cognitive Function." Journal of Microbiome Science, 12(4), 289-305.', 'Patel, J. & Rodriguez, T. (2022). "Biofilm Disruption Strategies for Enhanced Probiotic Colonization." Frontiers in Microbiology, 13:789456.', 'Kim, L. et al. (2023). "Gut-Derived Metabolites and Their Impact on Neuroinflammatory Pathways." Nature Neuroscience, 26(8), 1203-1215.']
    },
    'microbiota-reset-protocol': {
      id: 'microbiota-reset-protocol',
      title: 'CalBiome Launches Revolutionary Microbiota Reset Protocol',
      date: 'June 15, 2023',
      author: 'Dr. Michael Patel',
      authorTitle: 'Chief Scientific Officer at CalBiome',
      readTime: '5 min read',
      featuredImage: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
      tags: ['Product Launch', 'Microbiome', 'Gut Health', 'Biofilm'],
      excerpt: 'CalBiome announces the release of its groundbreaking Microbiota Reset Protocol, a comprehensive approach to gut health backed by years of research from the California Institute for Microbiome Research (CalMiR).',
      content: [{
        type: 'paragraph',
        text: 'CalBiome is proud to announce the launch of its groundbreaking Microbiota Reset Protocol, a revolutionary approach to gut health that addresses one of the most overlooked aspects of microbiome science: biofilm disruption and microbial recolonization.'
      }, {
        type: 'heading',
        text: 'A New Paradigm in Gut Health'
      }, {
        type: 'paragraph',
        text: "Traditional probiotic approaches often fail to deliver consistent results because they don't address the fundamental issue of biofilms – protective structures that shield harmful bacteria from both the immune system and beneficial probiotics. The Microbiota Reset Protocol represents a paradigm shift in microbiome health by combining targeted biofilm disruptors with evidence-based probiotic strains."
      }, {
        type: 'quote',
        text: '"We\'ve known for years that simply adding probiotics to an unhealthy gut environment is like planting seeds in concrete," explains Dr. Michael Patel, Chief Scientific Officer at CalBiome. "Our protocol first creates the optimal conditions for beneficial bacteria to thrive, then introduces carefully selected strains that can establish lasting colonies."'
      }, {
        type: 'image',
        url: 'https://images.unsplash.com/photo-1530026186672-2cd00ffc50fe?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
        caption: 'The Microbiota Reset Protocol includes a comprehensive guide and supplement regimen'
      }, {
        type: 'heading',
        text: 'Science-Backed Development'
      }, {
        type: 'paragraph',
        text: 'The protocol is the result of five years of intensive research at CalMiR, including in vitro studies, animal models, and human pilot trials. Key findings demonstrated that the combination of N-acetylcysteine (NAC), tomato phytonutrients, and specialized Bacillus laterosporus spores effectively disrupted pathogenic biofilms while preserving beneficial microbial communities.'
      }, {
        type: 'paragraph',
        text: 'Following the reset phase, a carefully formulated probiotic blend containing multiple evidence-based strains helps reestablish a healthy gut ecosystem, supported by essential cofactor nutrients that optimize microbial metabolism and function.'
      }, {
        type: 'heading',
        text: 'Comprehensive Approach'
      }, {
        type: 'paragraph',
        text: "The Microbiota Reset Protocol goes beyond supplements alone. Each kit includes a structured 30-day program guide with dietary recommendations, lifestyle modifications, and tracking tools to maximize results. This holistic approach reflects CalBiome's commitment to translating complex microbiome science into practical, user-friendly solutions."
      }, {
        type: 'paragraph',
        text: 'All components of the protocol have been independently validated through the Ukweli Certified™ program, ensuring that they meet rigorous standards for purity, potency, and evidence-based formulation.'
      }, {
        type: 'heading',
        text: 'Early Results and Availability'
      }, {
        type: 'paragraph',
        text: 'Preliminary feedback from healthcare practitioners who participated in pre-launch testing has been overwhelmingly positive. Users reported improvements in digestive comfort, regularity, energy levels, and mental clarity within the first two weeks of the program.'
      }, {
        type: 'paragraph',
        text: "The Microbiota Reset Protocol is now available directly through CalBiome's website and select healthcare practitioners. The company has also announced plans to make the protocol available through the Afya Program, which adapts evidence-based health interventions for underserved communities worldwide."
      }, {
        type: 'quote',
        text: '"This launch represents a significant milestone in our mission to translate cutting-edge microbiome research into practical solutions that improve people\'s lives," says Dr. Patel. "We\'re excited to see the impact it will have on gut health and overall wellness across diverse populations."'
      }],
      references: ['Patel, M. et al. (2023). "Biofilm Disruption and Microbial Recolonization: A Two-Phase Approach to Microbiome Restoration." Journal of Functional Medicine, 18(2), 112-128.', 'Nguyen, A. & Patel, M. (2022). "N-acetylcysteine and Tomato Phytonutrients in Biofilm Management: Mechanisms and Applications." Integrative Medicine Research, 11:345-359.', 'CalMiR Research Group (2023). "Pilot Trial Results: The Microbiota Reset Protocol in Individuals with Gut Dysbiosis." Internal Research Publication, California Institute for Microbiome Research.']
    }
  };
  // Get the article based on the articleId parameter, or default to the first article
  const article = articles[articleId] || Object.values(articles)[0];
  // Sample related articles
  const relatedArticles = [{
    id: 'probiotics-depression',
    title: 'New Study Links Probiotic Use to Reduced Depression Symptoms',
    date: 'August 28, 2023',
    image: 'https://images.unsplash.com/photo-1576086213369-97a306d36557?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    excerpt: 'Clinical trial shows significant improvement in depression symptoms with targeted probiotic therapy.'
  }, {
    id: 'microbiome-aging',
    title: 'Gut Microbiome Diversity May Predict Healthy Aging, Study Finds',
    date: 'August 10, 2023',
    image: 'https://images.unsplash.com/photo-1523740856324-f2ce89135981?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    excerpt: 'Research suggests maintaining diverse gut bacteria could be key to longevity and cognitive health in aging populations.'
  }, {
    id: 'diet-microbiome',
    title: 'Mediterranean Diet Promotes Beneficial Gut Bacteria, Research Shows',
    date: 'July 15, 2023',
    image: 'https://images.unsplash.com/photo-1526470608268-f674ce90ebd4?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
    excerpt: 'Study demonstrates how specific dietary patterns can shape microbiome composition for better health outcomes.'
  }];
  return <div className="min-h-screen bg-white" data-id="element-246" data-mp="1e52w2s8n93vc">
      <Navbar data-id="element-247" data-mp="1e65l0umeow8m" />
      {/* Article Header */}
      <div className="relative bg-gradient-to-r from-teal-900 to-teal-800 text-white py-16" data-id="element-248" data-mp="21ux8j8p1wqf2">
        <div className="absolute inset-0 overflow-hidden" data-id="element-249" data-mp="26u9t1msg9leh">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1532938911079-1b06ac7ceec7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center opacity-20" data-id="element-250" data-mp="26trxksdm3b7m"></div>
        </div>
        <div className="relative container mx-auto px-4 max-w-7xl" data-id="element-251" data-mp="1wy6sgld8xutn">
          <div className="max-w-3xl" data-id="element-252" data-mp="ggkfmgx372qx">
            <Link to="/news" className="inline-flex items-center text-teal-100 hover:text-white mb-6" data-id="element-253" data-mp="lh70xryq3k0o">
              <ArrowLeft size={16} className="mr-2" data-id="element-254" data-mp="11peiztgwmb42" /> Back to News
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-255" data-mp="wxd03op8dm52">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-teal-100 mb-6" data-id="element-256" data-mp="rzl86uol600v">
              <div className="flex items-center" data-id="element-257" data-mp="n08djnrh3onq">
                <Calendar size={16} className="mr-2" data-id="element-258" data-mp="mxcgs2rd360v" />
                <span data-id="element-259" data-mp="1km61i0omgm72">{article.date}</span>
              </div>
              <div className="flex items-center" data-id="element-260" data-mp="1fo4i1enaakv2">
                <User size={16} className="mr-2" data-id="element-261" data-mp="1fpfm7fkn5ump" />
                <span data-id="element-262" data-mp="1ap1g432lourb">{article.author}</span>
              </div>
              <div className="flex items-center" data-id="element-263" data-mp="1an6roes4ekih">
                <Clock size={16} className="mr-2" data-id="element-264" data-mp="15qgdjwzvlyzp" />
                <span data-id="element-265" data-mp="15poo73wvgxhv">{article.readTime}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4" data-id="element-266" data-mp="3vgtrliyyp7xp">
              {article.tags.map((tag, index) => <span key={index} className="bg-teal-800 bg-opacity-50 px-3 py-1 rounded-full text-sm" data-id="element-267" data-mp="3qeul407xduwj" data-mpi={index}>
                  {tag}
                </span>)}
            </div>
          </div>
        </div>
      </div>
      {/* Article Content */}
      <div className="container mx-auto px-4 py-12 max-w-7xl" data-id="element-268" data-mp="2fuzsa8dmva3a">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-269" data-mp="2ko2qittcv7pt">
          {/* Main Content */}
          <div className="lg:col-span-8" data-id="element-270" data-mp="2pl2n8e4bey49">
            <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100" data-id="element-271" data-mp="2po6xnt8diq3d">
              {/* Featured Image */}
              <img src={article.featuredImage} alt={article.title} className="w-full h-96 object-cover" data-id="element-272" data-mp="2ymalwxaqu0yv" />
              {/* Article Body */}
              <div className="p-8" data-id="element-273" data-mp="149b1mqvf4il9">
                <p className="text-xl text-gray-700 font-medium mb-8 leading-relaxed" data-id="element-274" data-mp="14acb4qwscx0v">
                  {article.excerpt}
                </p>
                {/* Render article content based on type */}
                <div className="prose prose-lg max-w-none text-gray-700" data-id="element-275" data-mp="117g988r1oplm">
                  {article.content.map((block, index) => {
                  switch (block.type) {
                    case 'heading':
                      return <h2 key={index} className="text-2xl font-bold text-teal-800 mt-8 mb-4" data-id="element-276" data-mp="165q1aahh3a30">
                            {block.text}
                          </h2>;
                    case 'paragraph':
                      return <p key={index} className="mb-6 leading-relaxed" data-id="element-277" data-mp="1txy8szsk88ua">
                            {block.text}
                          </p>;
                    case 'quote':
                      return <blockquote key={index} className="border-l-4 border-teal-500 pl-4 italic my-6 text-gray-600" data-id="element-278" data-mp="1yvgkgxxyp25z">
                            {block.text}
                          </blockquote>;
                    case 'image':
                      return <figure key={index} className="my-8" data-id="element-279" data-mp="2dhndlabu7kqq">
                            <img src={block.url} alt={block.caption} className="w-full rounded-lg" data-id="element-280" data-mp="2dg2kzq932bvi" />
                            <figcaption className="text-sm text-gray-500 mt-2 text-center" data-id="element-281" data-mp="23lk93xtija0m">
                              {block.caption}
                            </figcaption>
                          </figure>;
                    default:
                      return null;
                  }
                })}
                </div>
                {/* References */}
                <div className="mt-12 pt-8 border-t border-gray-200" data-id="element-282" data-mp="1kv2pqjuklqrx">
                  <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-283" data-mp="20ag1neo2s4s3">
                    References
                  </h3>
                  <ul className="list-decimal pl-5 space-y-2 text-gray-600 text-sm" data-id="element-284" data-mp="1w79wkacp8jn7">
                    {article.references.map((reference, index) => <li key={index} data-id="element-285" data-mp="1ma5b6cqogs7g" data-mpi={index}>{reference}</li>)}
                  </ul>
                </div>
                {/* Share */}
                <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-286" data-mp="1hdyfdcrzwx38">
                  <div className="flex items-center justify-between" data-id="element-287" data-mp="1cju7mrawol13">
                    <div data-id="element-288" data-mp="18j0ki05afa99">
                      <span className="text-gray-600 mr-4" data-id="element-289" data-mp="18kbj7ljvlrti">
                        Share this article:
                      </span>
                      <button className="text-gray-500 hover:text-teal-600 transition-colors" data-id="element-290" data-mp="13iw224ktk5ge">
                        <Share2 size={20} data-id="element-291" data-mp="yngn19b2s3ie" />
                      </button>
                    </div>
                    <div className="flex items-center text-sm text-gray-500" data-id="element-292" data-mp="tovorffdvtwl">
                      <User size={16} className="mr-2" data-id="element-293" data-mp="jicmebzyn1st" />
                      <div data-id="element-294" data-mp="ogw605r301i0">
                        <p data-id="element-295" data-mp="og4cd0h1odud">
                          Written by{' '}
                          <span className="font-medium text-teal-700" data-id="element-296" data-mp="tgilkludtttz">
                            {article.author}
                          </span>
                        </p>
                        <p data-id="element-297" data-mp="scy3rby1qqg">{article.authorTitle}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Related Articles */}
            <div className="mt-12" data-id="element-298" data-mp="aq0suh9juy8m">
              <h3 className="text-2xl font-bold text-teal-800 mb-6" data-id="element-299" data-mp="aozhscsjabqw">
                Related Articles
              </h3>
              <div className="grid md:grid-cols-3 gap-6" data-id="element-300" data-mp="fiu1hg852qpz">
                {relatedArticles.map(article => <div key={article.id} className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow" data-id="element-301" data-mp="3j63zqksu8ivw">
                    <img src={article.image} alt={article.title} className="w-full h-48 object-cover" data-id="element-302" data-mp="1k7m778g3y42z" />
                    <div className="p-4" data-id="element-303" data-mp="1t8k313ge75ah">
                      <p className="text-sm text-gray-500 mb-2" data-id="element-304" data-mp="1t8abhbfp8q2o">
                        {article.date}
                      </p>
                      <h4 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2" data-id="element-305" data-mp="wgq8rsh5camr">
                        {article.title}
                      </h4>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2" data-id="element-306" data-mp="15i6idy0508px">
                        {article.excerpt}
                      </p>
                      <Link to={`/news/${article.id}`} className="text-teal-600 hover:text-teal-800 font-medium flex items-center text-sm" data-id="element-307" data-mp="1absqdcvcvofe">
                        Read More <ArrowRight size={16} className="ml-1" data-id="element-308" data-mp="a9w1cjqmh2wn" />
                      </Link>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Sidebar */}
          <div className="lg:col-span-4" data-id="element-309" data-mp="184qz1l507wg">
            {/* Author Bio */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 mb-8" data-id="element-310" data-mp="3sgk1dv6tezvg">
              <div className="flex items-center mb-4" data-id="element-311" data-mp="3nh7h9i7bw6wk">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mr-4" data-id="element-312" data-mp="3niqwd5h90bwr">
                  <User size={32} className="text-teal-600" data-id="element-313" data-mp="3io4jexviqbak" />
                </div>
                <div data-id="element-314" data-mp="3dr4c6jmxn2hv">
                  <h3 className="font-bold text-gray-800" data-id="element-315" data-mp="3doia2zit9f71">{article.author}</h3>
                  <p className="text-gray-600 text-sm" data-id="element-316" data-mp="39r3wkvo3kl98">{article.authorTitle}</p>
                </div>
              </div>
              <p className="text-gray-600 text-sm" data-id="element-317" data-mp="2zr3cv8mbg34i">
                {article.author === 'Dr. Sarah Chen' ? 'Dr. Chen leads research initiatives at CalMiR focused on the microbiome-gut-brain connection. She has published over 30 peer-reviewed papers and holds a Ph.D. in Microbiology from Stanford University.' : 'Dr. Patel oversees all product development and scientific initiatives at CalBiome. With a background in biochemistry and microbiology, he specializes in translating complex research into practical health solutions.'}
              </p>
              <Link to={article.author === 'Dr. Sarah Chen' ? '/team/sarah-chen' : '/team/michael-patel'} className="mt-3 inline-flex items-center text-teal-600 hover:text-teal-800 text-sm font-medium" data-id="element-318" data-mp="38w1yf82na6sh">
                View full profile <ArrowRight size={14} className="ml-1" data-id="element-319" data-mp="3rsl0xeiy0wg2" />
              </Link>
            </div>
            {/* Newsletter Signup */}
            <div className="bg-teal-50 rounded-lg p-6 shadow-sm border border-teal-100 mb-8" data-id="element-320" data-mp="fqbx4vun2qb">
              <h3 className="font-bold text-teal-800 mb-3" data-id="element-321" data-mp="1l2zuc1zsox0h">Stay Updated</h3>
              <p className="text-gray-600 text-sm mb-4" data-id="element-322" data-mp="1g69e9enzq1f2">
                Subscribe to our newsletter for the latest research findings and
                product updates.
              </p>
              <form data-id="element-323" data-mp="1purdhrgmg0gl">
                <input type="email" placeholder="Your email address" className="w-full px-4 py-2 rounded-lg mb-3 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-600" data-id="element-324" data-mp="1pwlx92g5pmj3" />
                <button type="submit" className="w-full bg-teal-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-700 transition-colors" data-id="element-325" data-mp="1b64xron316ou">
                  Subscribe
                </button>
              </form>
            </div>
            {/* Popular Tags */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 mb-8" data-id="element-326" data-mp="2ikq7m19oans4">
              <h3 className="font-bold text-gray-800 mb-3" data-id="element-327" data-mp="3duwq47fb0lv2">Popular Topics</h3>
              <div className="flex flex-wrap gap-2" data-id="element-328" data-mp="3hvglb5gbhfek">
                {['Microbiome', 'Gut Health', 'Probiotics', 'Research', 'Biofilms', 'Nutrition', 'Brain Health', 'Immune System', 'Inflammation'].map((tag, index) => <span key={index} className="bg-gray-100 hover:bg-teal-50 px-3 py-1 rounded-full text-sm text-gray-700 hover:text-teal-700 cursor-pointer transition-colors" data-id="element-329" data-mp="4oybd5qqwwvq" data-mpi={index}>
                    {tag}
                  </span>)}
              </div>
            </div>
            {/* Featured Product */}
            <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100" data-id="element-330" data-mp="jc9pnfebauzj">
              <div className="bg-teal-800 text-white p-4 text-center" data-id="element-331" data-mp="oatafch660qb">
                <h3 className="font-bold" data-id="element-332" data-mp="28wjc6hvjrhy6">Featured Product</h3>
              </div>
              <div className="p-6" data-id="element-333" data-mp="23vc4eitemiri">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4GfsjSQSUFi6K4HMQubmAL/ddv.png" alt="CalBiome Microbiota Reset Protocol" className="w-full h-auto mb-4" data-id="element-334" data-mp="23yhthy219kn8" />
                <h4 className="font-bold text-gray-800 mb-2" data-id="element-335" data-mp="2rko9p7lm26oj">
                  Microbiota Reset Protocol
                </h4>
                <p className="text-gray-600 text-sm mb-4" data-id="element-336" data-mp="2mneoy5962j71">
                  Support your gut-brain connection with our science-backed
                  formula designed to restore microbial balance.
                </p>
                <Link to="/products/microbiota-reset-protocol" className="block text-center bg-teal-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-700 transition-colors" data-id="element-337" data-mp="2cuz6l6tyjslm">
                  Learn More
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-teal-900 to-teal-700 text-white py-16 mt-12" data-id="element-338" data-mp="18p4fbu4lrg5l">
        <div className="container mx-auto px-4 max-w-7xl" data-id="element-339" data-mp="txvm7b1hrcnp">
          <div className="max-w-3xl mx-auto text-center" data-id="element-340" data-mp="ywgkv613hoa1">
            <h2 className="text-3xl font-bold mb-6" data-id="element-341" data-mp="yuvrhjqiobdr">Want to Learn More?</h2>
            <p className="text-xl mb-8 text-teal-100" data-id="element-342" data-mp="kw5us57m0dpx">
              Explore our research initiatives and discover how our products can
              support your microbiome health.
            </p>
            <div className="flex flex-wrap gap-4 justify-center" data-id="element-343" data-mp="b144v75if7ma">
              <Link to="/research" className="bg-white text-teal-800 hover:bg-teal-50 transition-all duration-300 px-6 py-3 rounded-md font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-344" data-mp="fzfdxfnzde3g">
                Explore Our Research
              </Link>
              <Link to="/products" className="bg-transparent border-2 border-white text-white hover:bg-white/10 transition-all duration-300 px-6 py-3 rounded-md font-medium transform hover:-translate-y-1" data-id="element-345" data-mp="3uv7nd319mf3v">
                View Products
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer data-id="element-346" data-mp="hf9pyx2466w2" />
    </div>;
};
export default ArticlePage;